import React, { ReactNode, useEffect, useState } from "react";
import { map, omit } from "lodash";
import moment from "moment";
import RGL, { Layout, WidthProvider } from "react-grid-layout";
import {
  LayoutWithNode,
  ScheduleLayoutWithNode,
  convertToGrid,
  convertToSchedule,
  getTotalColumns,
  isReactElement,
} from "./helpers";
import "./styles.css";

const ReactGridLayout = WidthProvider(RGL);

const gridProps = {
  autoSize: false,
  className: "layout",
  draggableCancel: ".cancel-drag",
  maxRows: 1,
  preventCollision: true,
  rowHeight: 44,
  useCSSTransforms: true,
};

interface LayerFunctionParams {
  cols: number;
  isModifying: boolean;
  layout: LayoutWithNode[];
}

export interface LayerRenderFunction {
  (props: LayerFunctionParams): ReactNode;
}

export const defaultScheduleLayerRenderFn = ({
  cols,
  isModifying,
  layout,
}: LayerFunctionParams) => {
  return map(layout, item => {
    const { node } = item;
    if (!node) {
      return null;
    }
    if (!isReactElement(node)) {
      return node;
    }
    const clonedProps = {
      style: {
        pointerEvents: isModifying ? "none" : "all",
        zIndex: cols - item.w,
      },
    };
    return React.cloneElement(node, {
      key: item.i,
      ...clonedProps,
      ...omit(item, ["node", "x", "y", "h", "minH", "maxH"]),
    });
  });
};

interface ScheduleLayerProps {
  children: LayerRenderFunction;
  endAt?: moment.Moment;
  layout: ScheduleLayoutWithNode[];
  onChange?: (layout: ScheduleLayoutWithNode[]) => void;
  onChangeStart?: (layout: Layout | null) => void;
  onChangeStop?: () => void;
  sensitivity?: number;
  startAt?: moment.Moment;
  rerenderCache?: any[];
}

export const ScheduleLayer = ({
  children,
  endAt = moment().endOf("day"),
  layout: cachedLayout,
  onChange,
  onChangeStart,
  onChangeStop,
  sensitivity = 3,
  startAt = moment().startOf("day"),
  rerenderCache = [],
}: ScheduleLayerProps) => {
  const [isModifying, setIsModifying] = useState(false);
  const [currentItem, setCurrentItem] = useState<Layout | null>(null);
  const [maxMovement, setMaxMovment] = useState(0);
  const cols = getTotalColumns({ endAt, startAt });
  const [layout, setLayout] = useState(
    convertToGrid({ cols, endAt, layout: cachedLayout, startAt }),
  );
  useEffect(() => {
    setLayout(convertToGrid({ cols, endAt, layout: cachedLayout, startAt }));
  }, [
    cachedLayout.map(layoutItem => layoutItem.i).join("|"),
    endAt.toISOString(),
    startAt.toISOString(),
    ...rerenderCache,
  ]);
  useEffect(() => {
    if (isModifying && onChangeStart) {
      onChangeStart(currentItem);
    }
  }, [isModifying]);
  const handleChange = (newLayout: Layout[]) => {
    const newLayoutWithNodes = newLayout.map((item, index) => {
      const oldItem = layout[index];
      return {
        ...oldItem,
        ...item,
      };
    });
    setLayout(newLayoutWithNodes);
    if (onChange) {
      onChange(
        convertToSchedule({
          cols,
          endAt,
          layout: newLayoutWithNodes,
          startAt,
        }),
      );
    }
  };
  // TLDR: Apple branded Multi-Touch interfaces do not play nicely with React-Grid-Layout.
  // We want to prevent small jitters caused by your finger when clicking being intepreted as a drag action.
  // 3px of sensitivity does it for me, but we might need to expand this to 5 or something.
  const handleMovement = (event: MouseEvent) => {
    const deltaMovement = Math.abs(event.movementX) + Math.abs(event.movementY);
    if (deltaMovement > maxMovement) {
      setMaxMovment(deltaMovement);
      setIsModifying(deltaMovement > sensitivity);
    } else {
      setIsModifying(maxMovement > sensitivity);
    }
  };
  const handleStop = () => {
    setCurrentItem(null);
    setMaxMovment(0);
    requestAnimationFrame(() => setIsModifying(false));
    if (onChangeStop) {
      onChangeStop();
    }
  };
  const layerFunctionParams = {
    cols,
    isModifying,
    layout,
  };
  const wrappedChildren = children && children({ ...layerFunctionParams });
  return (
    <ReactGridLayout
      {...gridProps}
      cols={cols}
      compactType={null}
      containerPadding={[0, 0]}
      layout={layout}
      margin={[0, 0]}
      onDrag={(newLayout, oldItem, newItem, placeholder, event) => {
        handleMovement(event);
      }}
      onDragStart={(newLayout, oldItem, newItem) => {
        setCurrentItem(newItem);
      }}
      onDragStop={(newLayout: Layout[]) => {
        handleChange(newLayout);
        handleStop();
      }}
      onResize={(newLayout, oldItem, newItem, placeholder, event) => {
        handleMovement(event);
      }}
      onResizeStart={(newLayout, oldItem, newItem) => {
        setCurrentItem(newItem);
      }}
      onResizeStop={(newLayout: Layout[]) => {
        handleChange(newLayout);
        handleStop();
      }}
    >
      {wrappedChildren}
    </ReactGridLayout>
  );
};
